<template>
  <div class="measurement-item-wrapper" :class="{ layout, 'overflow-y-auto': totalPages === 1 }">
    <div class="swiper" ref="swiper">
      <div class="swiper-wrapper">
        <div v-for="(_, j) in totalPages" :key="j" class="swiper-slide">
          <div
            v-for="(m, i) in measurements.slice(j * countPerPage, (j + 1) * countPerPage)"
            :key="i"
            class="measurement-item"
            :class="{
              active: activeMeasurement === m,
              highlight: activeMeasurement !== m && highlighMeasurement(m),
            }"
            @click="$emit('click', m)"
          >
            <div class="measurement-name">
              <span>
                {{ getMeasurementName(m) }}
              </span>
              <span v-if="isCompare" class="measurement-lr">{{
                m.isLeft ? 'L' : m.isRight ? 'R' : ''
              }}</span>
            </div>

            <div v-if="isCompare" class="flex flex-row">
              <span
                v-if="useInch && isLine(m)"
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m.another) }"
                >{{ m.another.value | toInch | toFixed }}</span
              >
              <span
                v-else
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m.another) }"
                >{{ m.another.value | toFixed }}</span
              >
              <img class="mr-2" :src="getComparisonIcon(m)" />
              <span
                v-if="useInch && isLine(m)"
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m) }"
                >{{ m.value | toInch | toFixed }}</span
              >
              <span
                v-else
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m) }"
                >{{ m.value | toFixed }}</span
              >
            </div>
            <div v-else class="flex flex-row gap-1">
              <span v-if="m.rightMes" class="measurement-lr">L</span>
              <span
                v-if="useInch && isLine(m)"
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m) }"
                >{{ m.value | toInch | toFixed }}</span
              >
              <span
                v-else
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m) }"
                >{{ m.value | toFixed }}</span
              >
              <span v-if="m.rightMes" class="measurement-lr">R</span>
              <span
                v-if="useInch && isLine(m) && m.rightMes"
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m.rightMes) }"
                >{{ m.rightMes.value | toInch | toFixed }}</span
              >
              <span
                v-else-if="m.rightMes"
                class="measurement-value"
                :class="{ 'normal-value': isNormalValue(m.rightMes) }"
                >{{ m.rightMes.value | toFixed }}</span
              >
            </div>
            <div class="measurement-note">
              <span>
                {{ getMeasurementNote(m) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <step-dots
      v-if="totalPages > 1"
      :step="currentPage"
      :steps="totalPages"
      class="mx-auto justify-center mb-1"
      style="margin-top: 1rem;"
    />
  </div>
</template>

<script>
import 'swiper/swiper.min.css';
import Swiper from 'swiper';
import StepDots from '@/components/common/StepDots.vue';
import SizeXtName from '@/modules/sizext-name';

import valueUpSvg from '@/assets/common/value-up.svg';
import valueDownSvg from '@/assets/common/value-down.svg';
import valueEqualSvg from '@/assets/common/value-equal.svg';

const INCH = 0.393700787;

export default {
  name: 'MeasurementPage',
  props: {
    measurements: Array,
    tags: Array,
    countPerPage: { default: 5, type: Number },
    activeMeasurement: Object,
    useInch: Boolean,
    layout: String,
    isCompare: Boolean,
  },
  components: {
    StepDots,
  },
  data: () => ({
    pageIndex: 0,
  }),
  beforeDestroy() {
    this.destroySwiper();
  },
  mounted() {
    this.initSwiper();
  },
  methods: {
    initSwiper() {
      this.destroySwiper();

      if (this.totalPages === 1) return;

      this.$swiper = new Swiper(this.$refs.swiper);
      this.$swiper.on('activeIndexChange', () => {
        this.pageIndex = this.$swiper.activeIndex;
      });
    },
    destroySwiper() {
      if (!this.$swiper) return;
      this.$swiper.destroy();
      this.$swiper = null;
      this.pageIndex = 0;
    },
    getComparisonIcon(m) {
      if (Math.abs(m.value - m.another.value) < 0.01) return valueEqualSvg;
      if (m.value < m.another.value) return valueDownSvg;
      if (m.value > m.another.value) return valueUpSvg;
      return valueEqualSvg;
    },
    getMeasurementName(m) {
      // There are three types of measurement names (in order of priority):
      // 1. i18n nameKey: The i18n translation built into the app
      // 2. customName: The custom name from MTM
      // 3. sizextName: The name retrieved from the /api/v1/scan_records/size_xt_name translation table, which is also the default name on MTM
      const i18nName = m.nameKey ? this.$i18n.t(`measurement.${m.nameKey}`) : null;
      const name = i18nName || m.customName || SizeXtName.getName(m.key, m.subKey);

      // dailybelle custom postfix
      let postfix = '';
      if (Number.isFinite(m.value) && m.key === 'C19 Spine Degree') {
        const value = parseFloat(m.value.toFixed(2));
        if (value >= 0.5) {
          postfix = ' (往左偏)';
        } else if (value <= -0.5) {
          postfix = ' (往右偏)';
        }
      }
      if (m.key === 'C19 Right Neck to Ankle Degree') {
        if (Array.isArray(this.tags)) {
          if (this.tags.includes('骨盆前傾')) {
            postfix = ' (前傾)';
          } else if (this.tags.includes('骨盆後傾')) {
            postfix = ' (後傾)';
          }
        }
      }

      if (m.type === 'angle') return `${name} (°)${postfix}`;
      if (m.type === 'volume') return `${name} (cc)${postfix}`;
      return name;
    },
    getMeasurementNote(m) {
      const { note } = m;
      return note || '';
    },
    getMeasurementThreshold(m) {
      const { threshold } = m;
      if (Number.isFinite(threshold)) return threshold;
      return null;
    },
    isLine(m) {
      return m.type === 'line';
    },
    highlighMeasurement(m) {
      const threshold = this.getMeasurementThreshold(m);
      if (threshold && !this.isNormalValue(m)) return true;

      if (!this.tags?.length || !m?.tag) return false;

      const tagList = m.tag
        .split(',')
        .map(t => t.trim())
        .filter(t => t);
      return tagList.some(tag => this.tags.includes(tag));
    },
    isNormalValue(m) {
      if (!m) return false;
      const threshold = this.getMeasurementThreshold(m);
      if (threshold === null || !Number.isFinite(m.value)) {
        return false;
      }
      return Math.abs(m.value) < threshold;
    },
  },
  computed: {
    currentPage() {
      return this.pageIndex + 1;
    },
    totalPages() {
      return Math.ceil(this.measurements.length / this.countPerPage);
    },
  },
  filters: {
    toFixed(value) {
      if (!Number.isFinite(value)) return '--';
      const val = parseFloat(value);
      if (val > 100) return val.toFixed(1);
      return val.toFixed(2);
    },
    toInch(value) {
      return value * INCH;
    },
  },
  watch: {
    totalPages() {
      this.$nextTick(() => {
        this.$swiper.update();
        this.$swiper.slideTo(0);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.measurement-item-wrapper {
  @apply flex flex-1 flex-col w-full;
}

.measurement-item {
  --normal-color: #8c8c8c;
  @apply w-full flex items-center p-4 justify-between relative;
  height: 44px;
  color: var(--normal-color);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  border: 1px solid #bfbfbf;
  border-radius: 8px;

  &.active {
    color: #fff;
    background: var(--brand-gradient-dark);

    .measurement-lr {
      color: #fff;
    }
  }

  &.highlight {
    color: #bc7b8f;
  }

  .measurement-lr {
    color: #4658ff;
  }

  .measurement-name {
    width: 55%;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .measurement-note {
    position: absolute;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    bottom: -1px;
    width: 55%;
    font-size: 10px;
  }

  .measurement-value {
    width: 48px;
    &.normal-value {
      color: var(--normal-color);
    }
  }
}

.swiper {
  @apply w-full h-full;
}

.swiper-slide {
  @apply flex flex-col w-full h-auto px-6;
  gap: 12px;
}

.swiper-pagination {
  @apply text-center;
}
</style>
